import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'summary',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

function isTextEntry(element: HTMLElement): boolean {
  return element instanceof HTMLInputElement
    || element instanceof HTMLSelectElement
    || element instanceof HTMLTextAreaElement
    || element.isContentEditable
}

interface Props {
  /** Modal blocks the surface underneath. A non-modal drawer is still a viewport
   * surface, but it leaves the page live, unblurred and outside its focus cycle. */
  modal: boolean
  width: number
  label: string
  onClose: () => void
  children: ReactNode
}

export function DetailOverlay({ modal, width, label, onClose, children }: Props) {
  const panel = useRef<HTMLDivElement | null>(null)
  const opener = useRef<HTMLElement | null>(null)

  // Read through a ref so a new handler identity never restarts the effect: the
  // panel stays mounted across a content swap, and so does everything below.
  const close = useRef(onClose)
  close.current = onClose

  useEffect(() => {
    const node = panel.current
    const outside = (target: EventTarget | null): target is HTMLElement => (
      target instanceof HTMLElement && target !== document.body && !(node?.contains(target) ?? false)
    )

    // The opener is wherever the operator last came from outside the panel, not
    // whatever happened to be focused when the panel first mounted. The card swaps
    // content in place, so each swap arrives from a different row, and the row it
    // was first opened from is no longer the one to go back to.
    if (outside(document.activeElement)) opener.current = document.activeElement
    const onFocusIn = (event: FocusEvent) => { if (outside(event.target)) opener.current = event.target }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (event.defaultPrevented) return
        // Non-modal leaves the surface underneath live, so an Escape aimed at a
        // control out there belongs to that control - clearing a filter is not
        // closing the card. An Escape inside the panel, or on no element at all,
        // is the card's own.
        if (!modal && outside(event.target) && isTextEntry(event.target)) return
        close.current()
        return
      }
      if (!modal || event.key !== 'Tab' || !panel.current) return
      const focusable = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE))
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement
      if (event.shiftKey && (active === first || !panel.current.contains(active))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('focusin', onFocusIn)
    window.addEventListener('keydown', onKeyDown)
    const frame = requestAnimationFrame(() => panel.current?.focus())
    return () => {
      document.removeEventListener('focusin', onFocusIn)
      window.removeEventListener('keydown', onKeyDown)
      cancelAnimationFrame(frame)
      // Only take focus back if it is the panel's to give: focus the operator has
      // already moved onto the live surface stays where they put it, and an opener
      // that has since left the DOM is not somewhere to send it.
      const active = document.activeElement
      const held = active === null || active === document.body || (node?.contains(active) ?? false)
      if (held && opener.current?.isConnected) opener.current.focus()
    }
  }, [modal])

  const box = (
    <div
      ref={panel}
      tabIndex={-1}
      role={modal ? 'dialog' : 'region'}
      aria-modal={modal ? true : undefined}
      aria-label={label}
      data-testid="detail-overlay"
      data-modal={modal ? 'true' : 'false'}
      className="flex h-full min-h-0 w-full flex-col overflow-hidden shadow-[-24px_0_70px_rgba(20,17,13,0.16)] outline-none"
    >
      {children}
    </div>
  )

  if (!modal) {
    // Only the panel is portalled. There is no full-viewport wrapper or scrim, so
    // the left result region remains a genuine pointer and keyboard target.
    return createPortal(
      <div data-entrance="right-edge" style={{ maxWidth: `${width}px` }} className="warehouse-drawer-enter fixed right-0 top-0 z-[55] h-dvh w-full sm:w-[52vw]">
        {box}
      </div>,
      document.body,
    )
  }

  return createPortal(
    <div className="fixed inset-0 z-40">
      <div aria-hidden="true" onClick={() => close.current()} className="absolute inset-0 bg-onyx/24 backdrop-blur-[1px]" />
      <div style={{ width: `min(${width}px, 100%)` }} className="absolute inset-y-0 right-0">{box}</div>
    </div>,
    document.body,
  )
}
